/**
 * Docs.tsx — In-app protocol documentation.
 *
 * Deliberately self-contained (no i18n, no data fetching). The goal is that
 * a user or auditor can land on `/docs` and read the full protocol without
 * leaving the app or chasing external links. Sections are anchor-linkable
 * (`/docs#arbitration`) so we can deep-link from the rest of the UI.
 *
 * Content source: this is the canonical user-facing spec for MotoSettle
 * v1.0.0. If you change behavior on-chain, update this file in the same PR.
 */

import React, { useEffect, useMemo } from "react";

// ─── Table-of-contents config ─────────────────────────────────────────────────
const TOC: Array<{ id: string; title: string }> = [
  { id: "overview",      title: "1. Overview" },
  { id: "actors",        title: "2. Actors" },
  { id: "lifecycle",     title: "3. Escrow lifecycle" },
  { id: "funding",       title: "4. Funding the escrow" },
  { id: "release",       title: "5. Happy path: release" },
  { id: "dispute",       title: "6. Dispute path" },
  { id: "arbitration",   title: "7. Arbitration" },
  { id: "expiry",        title: "8. Expiry & timeouts" },
  { id: "fees",          title: "9. Fees, discounts & MOTO" },
  { id: "reputation",    title: "10. Reputation" },
  { id: "security",      title: "11. Security model & invariants" },
  { id: "architecture",  title: "12. Contract architecture" },
  { id: "governance",    title: "13. Governance surface" },
  { id: "glossary",      title: "14. Glossary" },
];

// ─── Reusable presentational helpers ──────────────────────────────────────────
const Section: React.FC<{ id: string; title: string; children: React.ReactNode }> = ({
  id, title, children,
}) => (
  <section id={id} style={{ scrollMarginTop: "5rem", marginBottom: "3rem" }}>
    <h2 style={{ borderBottom: "1px solid #2a2a2a", paddingBottom: "0.5rem" }}>{title}</h2>
    {children}
  </section>
);

const Callout: React.FC<{ kind: "info" | "warn" | "ok"; children: React.ReactNode }> = ({
  kind, children,
}) => {
  const colors = {
    info: { bg: "#1a2233", border: "#3b82f6" },
    warn: { bg: "#2a2010", border: "#f59e0b" },
    ok:   { bg: "#102818", border: "#10b981" },
  }[kind];
  return (
    <div style={{
      background: colors.bg, borderLeft: `4px solid ${colors.border}`,
      padding: "0.75rem 1rem", margin: "1rem 0", borderRadius: 4,
    }}>{children}</div>
  );
};

const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code style={{
    background: "#1a1a1a", padding: "0.15rem 0.4rem",
    borderRadius: 3, fontSize: "0.9em",
  }}>{children}</code>
);

// ─── Page ─────────────────────────────────────────────────────────────────────
export function DocsPage() {
  // Smooth-scroll to hash anchor if present (e.g. /docs#arbitration).
  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const toc = useMemo(() => TOC, []);

  return (
    <div style={{ display: "flex", gap: "2rem", maxWidth: 1200, margin: "0 auto" }}>
      {/* Sticky side-nav */}
      <nav style={{
        position: "sticky", top: "5rem", alignSelf: "flex-start",
        minWidth: 220, fontSize: "0.9rem",
      }}>
        <div style={{ opacity: 0.6, marginBottom: "0.75rem", fontSize: "0.8rem", letterSpacing: 1 }}>
          CONTENTS
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, lineHeight: 1.9 }}>
          {toc.map(s => (
            <li key={s.id}>
              <a href={`#${s.id}`} style={{ color: "#9ca3af", textDecoration: "none" }}>
                {s.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <article style={{ flex: 1, lineHeight: 1.7 }}>
        <header style={{ marginBottom: "2rem" }}>
          <h1 style={{ marginBottom: 0 }}>MotoSettle Protocol Documentation</h1>
          <p style={{ opacity: 0.7, marginTop: "0.25rem" }}>
            Trustless peer-to-peer escrow on OP_NET &middot; Version 1.0.0
          </p>
        </header>

        <Section id="overview" title="1. Overview">
          <p>
            <strong>MotoSettle</strong> is a non-custodial escrow protocol that lets two
            parties trade an <Code>OP-20</Code> token against an off-chain
            counter-value (fiat wire transfer, a physical good, a licence key —
            anything that is not settled on-chain) with optional third-party
            arbitration.
          </p>
          <p>
            The protocol consists of five AssemblyScript contracts on OP_NET
            (Bitcoin L2): <Code>EscrowCore</Code>, <Code>FeePolicy</Code>,
            <Code>ArbitratorRegistry</Code>, <Code>UserReputation</Code> and
            the convenience <Code>MotoSettleRouter</Code>. No contract is
            upgradeable; <Code>EscrowCore</Code> has no owner at all.
          </p>
          <Callout kind="ok">
            <strong>Core guarantee.</strong> Once funded, the only addresses
            that can move an escrow&rsquo;s tokens are the buyer, the seller, or the
            pre-selected arbitrator — never the deployer, never a governance
            multisig, never a protocol upgrade.
          </Callout>
        </Section>

        <Section id="actors" title="2. Actors">
          <ul>
            <li>
              <strong>Buyer</strong> — creates the escrow, locks the tokens,
              releases them to the seller when satisfied, or opens a dispute.
              Also chooses the arbitrator at creation.
            </li>
            <li>
              <strong>Seller</strong> — is named as beneficiary, provides the
              off-chain value, and can also open a dispute if the buyer
              refuses to release.
            </li>
            <li>
              <strong>Arbitrator</strong> — registered in
              <Code>ArbitratorRegistry</Code>. Only called into action if a
              dispute is raised. Their verdict is final on-chain.
            </li>
            <li>
              <strong>Operator</strong> — an optional delegate address the
              buyer approves in advance to fund escrows on their behalf
              (typically the <Code>MotoSettleRouter</Code>, used for the
              one-click <em>create-and-fund</em> UX). Operators
              <em> cannot</em> release, resolve or otherwise move funds.
            </li>
            <li>
              <strong>Governance</strong> — owner of the peripheral contracts
              (<Code>FeePolicy</Code>, <Code>ArbitratorRegistry</Code>,
              <Code>UserReputation</Code>). Can tune parameters within
              hard-coded caps but has zero power over funds in escrow.
            </li>
          </ul>
        </Section>

        <Section id="lifecycle" title="3. Escrow lifecycle">
          <p>Every escrow is a finite-state machine with these states:</p>
          <pre style={{
            background: "#1a1a1a", padding: "1rem", borderRadius: 6, overflowX: "auto",
            fontSize: "0.85rem",
          }}>{`
     CREATED (0)
        │ buyer (or approved operator) calls fundEscrow
        ▼
     FUNDED (1) ─────────── buyer calls releaseFunds ──────► RELEASED (3)
        │                                                     (terminal)
        │ buyer or seller calls raiseDispute
        ▼
     DISPUTED (2) ───── arbitrator resolves ─────► RESOLVED (4)
                                                    (terminal)
     CREATED or FUNDED, fundDeadline/tradeDeadline passed
        └── anyone calls expireEscrow ──────────► EXPIRED (5)
                                                    (terminal)
     FUNDED, both parties signal agreement
        └── cooperative unwind ─────────────────► CANCELLED (7)
                                                    (terminal)
          `.trim()}</pre>
          <p>
            There are seven valid states; state <Code>6</Code> is reserved and
            never emitted. The terminal set is <Code>&#123;3, 4, 5, 7&#125;</Code>.
          </p>
        </Section>

        <Section id="funding" title="4. Funding the escrow">
          <p>
            Creation alone reserves an escrow ID but moves no tokens. The
            buyer (or an operator they approved via
            <Code>approveOperator(operator, true)</Code>) must then call
            <Code>fundEscrow(escrowId, motoBalance)</Code>, which:
          </p>
          <ol>
            <li>Pulls <Code>amount</Code> tokens from the buyer via <Code>transferFrom</Code>.</li>
            <li>Computes the protocol fee via <Code>FeePolicy.computeFee</Code> and records it.</li>
            <li>Transitions the escrow to <Code>FUNDED</Code>.</li>
          </ol>
          <Callout kind="info">
            The <Code>motoBalance</Code> argument is <em>self-reported</em> and only
            used for the discount calculation (§9). It is never checked
            against the user&rsquo;s actual MOTO balance because discounts are
            bounded by a hard cap regardless.
          </Callout>
          <p>
            If the token has frozen approvals, fees-on-transfer, or returns
            <Code>false</Code> instead of reverting, the call will revert at
            <Code>transferFrom</Code> — the OP-20 <Code>transferFrom</Code> wrapper
            validates boolean returns strictly (Finding F-13).
          </p>
        </Section>

        <Section id="release" title="5. Happy path: release">
          <p>
            When the buyer is satisfied off-chain, they call
            <Code>releaseFunds(escrowId)</Code>. This:
          </p>
          <ul>
            <li>Transfers <Code>amount − fee</Code> to the seller.</li>
            <li>Transfers <Code>fee</Code> to <Code>FeePolicy</Code>, which books it under the escrow&rsquo;s token ledger.</li>
            <li>Records a positive trade for both parties in <Code>UserReputation</Code>.</li>
            <li>Transitions the escrow to <Code>RELEASED</Code>.</li>
          </ul>
          <Callout kind="warn">
            <strong>Only the buyer</strong> can release. Operators
            <em> cannot</em>. This is intentional — release is the one action
            a compromised operator must not be able to perform.
          </Callout>
        </Section>

        <Section id="dispute" title="6. Dispute path">
          <p>
            Either buyer or seller can call <Code>raiseDispute(escrowId)</Code>
            while the escrow is <Code>FUNDED</Code>. The escrow moves to
            <Code>DISPUTED</Code>, which disables further releases and starts
            the arbitration clock.
          </p>
          <p>
            Before finding F-1 was fixed, a malicious seller could raise a
            dispute at zero cost immediately after funding and grief the
            buyer. Today, <Code>raiseDispute</Code> additionally enforces
            that the disputing party has a non-zero stake at risk and
            debits an anti-griefing bond which is forfeit if the arbitrator
            rules against them. See the
            <a href="https://gitlab.com/acceleralabs/motosettle/-/blob/main/audit/HARDENING_CHANGELOG.md">
              {" "}hardening changelog
            </a>.
          </p>
        </Section>

        <Section id="arbitration" title="7. Arbitration">
          <p>
            The arbitrator named at creation is the <em>only</em> address that
            can resolve a dispute. They call
            <Code>resolveDispute(escrowId, durationBlocks)</Code> with a split
            <Code>(buyerResolved, sellerResolved)</Code> that sums to
            <Code>amount − arbFee</Code>. The arbitrator then earns their fee,
            and the case is stamped into <Code>ArbitratorRegistry</Code> for
            reputation purposes.
          </p>
          <p>
            Arbitrator fees are capped at <Code>5%</Code> of the escrow
            amount (governable downward, never upward). A misbehaving
            arbitrator cannot overwrite a prior verdict, cannot resolve an
            escrow they were not assigned to, and cannot take more than the
            fee they declared at registration.
          </p>
        </Section>

        <Section id="expiry" title="8. Expiry & timeouts">
          <p>
            Two deadlines are set at creation:
          </p>
          <ul>
            <li>
              <Code>fundDeadline</Code> — block height by which the buyer
              must fund. Missed &rarr; anyone can call
              <Code>expireEscrow</Code>; the escrow ends in
              <Code>EXPIRED</Code> with no token movement (it was never
              funded).
            </li>
            <li>
              <Code>tradeDeadline</Code> — block height by which the trade
              must conclude (release or dispute). Missed &rarr; anyone can
              expire; funds return to the buyer, minus a small protocol fee
              for the oracle-work consumed.
            </li>
          </ul>
          <Callout kind="info">
            Expiry is permissionless on purpose — it guarantees that a silent
            counter-party cannot grief the other by refusing to act. Anyone
            can progress a stuck escrow to its terminal state.
          </Callout>
        </Section>

        <Section id="fees" title="9. Fees, discounts & MOTO">
          <p>
            The protocol fee is computed by <Code>FeePolicy.computeFee</Code>:
          </p>
          <pre style={{ background: "#1a1a1a", padding: "1rem", borderRadius: 6 }}>{`
fee = amount × (baseFeeBps − discountBps) / 10 000
where
  baseFeeBps    ∈ [0, 500]              (default 100 = 1.00%)
  discountBps   = min(maxDiscBps, MOTO-proportional)
  maxDiscBps    ∈ [0, 200]              (default 100 = 1.00% off)
          `.trim()}</pre>
          <p>
            The effective fee is therefore always in
            <Code>[0, 5%]</Code> — this is enforced by invariant #6 and
            checked live by the testnet smoke test. Holders of MOTO receive
            a linear discount up to <Code>maxDiscBps</Code>; non-holders pay
            the full base fee.
          </p>
        </Section>

        <Section id="reputation" title="10. Reputation">
          <p>
            <Code>UserReputation</Code> counts, per address:
          </p>
          <ul>
            <li>Volume traded (in token-units, summed across all tokens).</li>
            <li>Trades won / lost.</li>
            <li>Disputes raised, won and lost.</li>
          </ul>
          <p>
            Reputation is append-only and transparent. It is not used inside
            any on-chain access-control check — front-ends may use it to
            flag low-reputation counter-parties, but the protocol itself
            never gates a user.
          </p>
        </Section>

        <Section id="security" title="11. Security model & invariants">
          <p>MotoSettle enforces the following invariants at every state transition. Each has a test suite and a threat-model entry; see the hardening changelog for the revert paths.</p>
          <ol>
            <li><strong>Conservation.</strong> For any terminal state, the sum of token flows out equals <Code>amount</Code> exactly. No dust is created or destroyed.</li>
            <li><strong>Authorization.</strong> Release requires the buyer; dispute requires buyer or seller; resolve requires the named arbitrator; expire is permissionless but only after the relevant deadline.</li>
            <li><strong>Monotonicity.</strong> State transitions are one-directional. Terminal states accept no further calls.</li>
            <li><strong>Fee bound.</strong> <Code>fee ≤ 5% × amount</Code> for all valid inputs (live-checked by smoke test §Invariant #6).</li>
            <li><strong>Operator isolation.</strong> Operators can fund but cannot release, dispute, or resolve — verified by regression test F-3.</li>
            <li><strong>Buyer ≠ Seller.</strong> Self-escrows are rejected at creation (F-5).</li>
            <li><strong>Strict OP-20.</strong> Non-standard tokens that return <Code>false</Code> from <Code>transfer</Code>/<Code>transferFrom</Code> cause an explicit revert rather than silent failure (F-13).</li>
            <li><strong>getEscrow encoding.</strong> The read-only accessor returns a stable 12-field struct whose shape is part of the ABI contract (F-12, checked live by smoke test §3).</li>
          </ol>
          <p>
            The full list of 13 hardening findings (F-1 … F-13) with
            severity, revert paths and regression tests lives in
            <Code>audit/HARDENING_CHANGELOG.md</Code>.
          </p>
        </Section>

        <Section id="architecture" title="12. Contract architecture">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #333" }}>
                <th style={{ textAlign: "left", padding: "0.5rem" }}>Contract</th>
                <th style={{ textAlign: "left", padding: "0.5rem" }}>Owner</th>
                <th style={{ textAlign: "left", padding: "0.5rem" }}>Role</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={{ padding: "0.5rem" }}><Code>EscrowCore</Code></td><td>none</td><td>FSM + custody of tokens in escrow.</td></tr>
              <tr><td style={{ padding: "0.5rem" }}><Code>FeePolicy</Code></td><td>governance</td><td>Computes fees, holds collected fees per-token.</td></tr>
              <tr><td style={{ padding: "0.5rem" }}><Code>ArbitratorRegistry</Code></td><td>governance</td><td>Approves arbitrators, caps fee-rates, books cases.</td></tr>
              <tr><td style={{ padding: "0.5rem" }}><Code>UserReputation</Code></td><td>governance</td><td>Stats, allowlist for writers.</td></tr>
              <tr><td style={{ padding: "0.5rem" }}><Code>MotoSettleRouter</Code></td><td>none</td><td>UX glue: create + fund in one transaction.</td></tr>
            </tbody>
          </table>
          <p style={{ marginTop: "1rem" }}>
            <Code>EscrowCore</Code> is the only contract that holds user tokens. Its
            dependencies on <Code>FeePolicy</Code>, <Code>ArbitratorRegistry</Code>
            and <Code>UserReputation</Code> are bound at construction time and
            cannot be changed. Replacing any of them requires redeploying the
            entire stack.
          </p>
        </Section>

        <Section id="governance" title="13. Governance surface">
          <p>The full set of governable parameters is:</p>
          <ul>
            <li><Code>FeePolicy.setBaseFeeBps(x)</Code> — capped at 500 bps.</li>
            <li><Code>FeePolicy.setMaxDiscountBps(x)</Code> — capped at 200 bps.</li>
            <li><Code>ArbitratorRegistry.setMaxArbitratorFeeRateBps(x)</Code> — capped at 500 bps.</li>
            <li><Code>ArbitratorRegistry.approve / revoke arbitrator</Code>.</li>
            <li><Code>UserReputation.addCaller / removeCaller</Code>.</li>
            <li>Two-phase <Code>transferOwnership</Code> on every owned contract.</li>
          </ul>
          <Callout kind="warn">
            <strong>Governance cannot:</strong> move tokens held in escrow,
            override a dispute, change <Code>EscrowCore</Code>&rsquo;s wiring,
            upgrade any contract, or freeze user balances. Attempting any of
            these reverts at the contract level, not the UI level.
          </Callout>
        </Section>

        <Section id="glossary" title="14. Glossary">
          <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "0.5rem 1rem" }}>
            <dt><Code>OP_NET</Code></dt><dd>Bitcoin L2 smart-contract runtime that executes AssemblyScript → WASM contracts.</dd>
            <dt><Code>OP-20</Code></dt><dd>OP_NET&rsquo;s standard fungible-token interface (the ERC-20 analogue).</dd>
            <dt><Code>bps</Code></dt><dd>Basis points — 1/100th of a percent. 500 bps = 5%.</dd>
            <dt><Code>MOTO</Code></dt><dd>The protocol discount token. Held by users, read by <Code>FeePolicy</Code> for fee discounts.</dd>
            <dt><Code>WIF</Code></dt><dd>Wallet Import Format — the private-key encoding used by OP_NET signers.</dd>
            <dt><Code>bech32m</Code></dt><dd>Address-encoding scheme; OP_NET contract addresses use the <Code>opt1…</Code> prefix.</dd>
          </dl>
        </Section>

        <footer style={{ marginTop: "3rem", paddingTop: "1rem", borderTop: "1px solid #2a2a2a", opacity: 0.6, fontSize: "0.85rem" }}>
          This documentation is versioned with the protocol — the source lives
          at <Code>frontend/src/pages/Docs.tsx</Code>. If you change contract
          behaviour, update this file in the same pull request.
        </footer>
      </article>
    </div>
  );
}
